// src\themes\tolica\components\SwiperList.tsx
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useId } from 'react';
import CustomButton from './CustomButton';

export default function SwiperList({ data, isHome = false }: SwiperListProps & { isHome?: boolean }) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const uid = useId().replace(/:/g, "-");

  const prevClass = `swiper-prev-${uid}`;
  const nextClass = `swiper-next-${uid}`;

  return (
    <div className={`w-full col-span-12 bg-primary-50 p-4 ${isHome ? 'sm:px-6 lg:px-12' : ''}`}>
      <div className="flex justify-between items-center mb-4">
        <Heading level={3} className="pb-2">
          {data.title}
        </Heading>

        <div className="flex items-center gap-3">
          {/* مشاهده همه */}
          {data.link &&
            <Link href={data.link} className="text-xs text-primary-600 hover:underline whitespace-nowrap">
              {dictionary.ui.pages.seeAll}
            </Link>
          }
          {/* خط جداکننده */}
          <span className="w-px h-4 bg-primary-300" />

          {/* دکمه‌های ناوبری */}
          <div className="flex gap-2">
            <CustomButton className={`${prevClass} w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center`}>
              <ChevronRight className="w-4.5 h-4.5 stroke-1" />
            </CustomButton>
            <CustomButton className={`${nextClass} w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center`}>
              <ChevronLeft className="w-4.5 h-4.5 stroke-1" />
            </CustomButton>
          </div>
        </div>
      </div>
      <Swiper
        modules={[Navigation]}
        spaceBetween={16}
        slidesPerView="auto"
        className="w-full"
        navigation={{
          prevEl: `.${prevClass}`,
          nextEl: `.${nextClass}`,
        }}
        style={{ paddingBottom: '50px' }}
      >
        {items.map((item, index) => {
          const Content = (
            <div className='relative overflow-hidden group'>
              <div
                className="w-full h-full mb-2 relative aspect-3/4 overflow-hidden"
              >
                {(item.image?.url ||
                  item.image_info?.url) && (
                    <Image
                      src={
                        item.image?.url ||
                        item.image_info?.url
                      }
                      alt={item.title || ''}
                      fill
                      className="object-cover transition duration-500 group-hover:scale-105"
                    />
                  )}
              </div>
              <div className="absolute inset-0 z-0" />
              <div className='absolute bottom-3 right-3 left-3'>
                <Heading className='text-primary-900' level={4}>{item.title}</Heading>
                <p className="text-primary-600">{item.description}</p>
              </div>
            </div>
          );

          return (
            <SwiperSlide
              key={index}
              style={{ width: '300px', height: '360px' }}
            >
              {item.link ? <Link href={item.link}>{Content}</Link> : Content}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}